import type { Document as ElasoDocument } from "@elaso/shared/types/db";
import {
	ArrowUpRight,
	ChevronLeft,
	ChevronRight,
	FileText,
	Loader2,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useStore } from "zustand";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { getDocumentIcon } from "@/components/ui/document-icons";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from "@/components/ui/sheet";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { useIsMobile } from "@/hooks/use-mobile";
import { ROLE_DISPLAY_NAMES, ROLE_PRIORITY_ORDER } from "@/lib/role-constants";
import { coreStore } from "@/lib/stores/core-store";
import { uiStore } from "@/lib/stores/ui-store";
import { cn } from "@/lib/utils";

const PROGRAM_ROLE_REGEX = /workspace_role:[^-]+--[^-]+--(.+)/;
const TEAM_ROLE_REGEX = /^team--/;
const COHORT_ROLE_REGEX = /^cohort--/;
const OI_TABLE_ROLE_REGEX = /^oi-table--/;

const extractRoleName = (roleKey: string | null): string | null => {
	if (!roleKey) return null;
	const match = roleKey.match(PROGRAM_ROLE_REGEX);
	return match ? match[1] : roleKey;
};

const getCategoryFromRole = (roleName: string): string | null => {
	if (TEAM_ROLE_REGEX.test(roleName)) return "Team Assignments";
	if (COHORT_ROLE_REGEX.test(roleName)) return "Cohort Assignments";
	if (OI_TABLE_ROLE_REGEX.test(roleName)) return "OI Table Assignments";
	return null;
};

type CategorizedDocs = { category: string; docs: ElasoDocument[] }[];

const DYNAMIC_CATEGORY_ORDER = [
	"Cohort Assignments",
	"Team Assignments",
	"OI Table Assignments",
];

const addToCategory = (
	map: Record<string, ElasoDocument[]>,
	key: string,
	doc: ElasoDocument
) => {
	if (!map[key]) map[key] = [];
	map[key].push(doc);
};

const sortDocsByName = (docs: ElasoDocument[]): ElasoDocument[] =>
	[...docs].sort((a, b) => a.name.localeCompare(b.name));

const buildOrderedCategories = (
	dynamicCategories: Record<string, ElasoDocument[]>,
	categories: Record<string, ElasoDocument[]>,
	uncategorized: ElasoDocument[]
): CategorizedDocs => {
	const result: CategorizedDocs = [];

	for (const role of ROLE_PRIORITY_ORDER) {
		if (categories[role]?.length > 0) {
			result.push({
				category: ROLE_DISPLAY_NAMES[role] || role,
				docs: sortDocsByName(categories[role]),
			});
		}
	}

	for (const category of DYNAMIC_CATEGORY_ORDER) {
		if (dynamicCategories[category]?.length > 0) {
			result.push({
				category,
				docs: sortDocsByName(dynamicCategories[category]),
			});
		}
	}

	if (uncategorized.length > 0) {
		result.push({ category: "Other", docs: sortDocsByName(uncategorized) });
	}

	return result;
};

const categorizeDocuments = (
	documents: Map<string, ElasoDocument>
): CategorizedDocs => {
	const categories: Record<string, ElasoDocument[]> = {};
	const dynamicCategories: Record<string, ElasoDocument[]> = {};
	const uncategorized: ElasoDocument[] = [];

	for (const doc of documents.values()) {
		const roleName = extractRoleName(doc.primaryRoleKey);
		if (!roleName) {
			uncategorized.push(doc);
			continue;
		}

		const dynamicCategory = getCategoryFromRole(roleName);
		if (dynamicCategory) {
			addToCategory(dynamicCategories, dynamicCategory, doc);
		} else if (ROLE_PRIORITY_ORDER.includes(roleName)) {
			addToCategory(categories, roleName, doc);
		} else {
			uncategorized.push(doc);
		}
	}

	return buildOrderedCategories(dynamicCategories, categories, uncategorized);
};

function DocumentsList({
	isCollapsed,
	setOpen,
}: {
	isCollapsed: boolean;
	setOpen?: (open: boolean) => void;
}) {
	const navigate = useStore(uiStore, (state) => state.navigate);
	const documents = useStore(coreStore, (state) => state.documents);
	const documentsLoading = useStore(
		coreStore,
		(state) => state.documentsLoading
	);
	// During a workspace switch the chain empties documents and only refetches
	// them at its final step, so treat the whole cold-load as "loading" to avoid
	// a "No documents" flash before document.list lands.
	const isColdLoading = useStore(coreStore, (state) => state.isColdLoading);
	// True once a COMPLETE document set exists, meaning document.list succeeded or
	// a complete set was hydrated from cache. Gating the render on this rather
	// than on `documents.size === 0` stops a partial set left by an incremental
	// create or delete broadcast from rendering "only the one doc" before the
	// first full document.list. See D2.3 / TECH-2944.
	const documentsLoaded = useStore(coreStore, (state) => state.documentsLoaded);
	const fetchDocuments = useStore(coreStore, (state) => state.fetchDocuments);
	const isMobile = useIsMobile();

	const categorizedDocuments = useMemo(
		() => categorizeDocuments(documents),
		[documents]
	);

	// Render the list ONLY once a complete set exists. Until then show a spinner
	// while a load is in flight, and show "No documents" only after a confirmed
	// complete-but-empty load.
	const isLoading = documentsLoading || isColdLoading;
	const showLoading = !documentsLoaded && isLoading;
	const showEmpty = documentsLoaded && documents.size === 0;

	const loadingNode = (
		<div className="flex items-center justify-center py-6">
			<Loader2 className="size-4 animate-spin text-muted-foreground" />
		</div>
	);
	const emptyNode = (
		<div className="px-2 py-6 text-center text-muted-foreground text-xs">
			No documents
		</div>
	);

	const handleDocumentClick = (
		doc: { id: string; isExternal: boolean; url: string },
		e: React.MouseEvent<HTMLButtonElement>
	) => {
		if (isMobile) {
			setOpen?.(false);
		}
		if (doc.isExternal) {
			window.open(doc.url, "_blank", "noopener,noreferrer");
		} else {
			navigate(
				{ type: "document", params: { documentId: doc.id } },
				{ newTab: e.metaKey || e.ctrlKey }
			);
		}
	};

	if (isCollapsed) {
		return (
			<Popover
				onOpenChange={(isOpen) => {
					if (isOpen) fetchDocuments();
				}}
			>
				<PopoverTrigger asChild>
					<Button className="size-8 p-0" variant="ghost">
						<FileText className="size-4" />
					</Button>
				</PopoverTrigger>
				<PopoverContent align="start" className="w-56 p-2" side="left">
					<div className="flex flex-col gap-1">
						<div className="px-2 py-1 font-medium text-muted-foreground text-xs">
							Documents
						</div>
						{showLoading && loadingNode}
						{showEmpty && emptyNode}
						{documentsLoaded &&
							categorizedDocuments.map(({ category, docs }) => (
								<div key={category}>
									<div className="mt-2 px-2 py-1 font-semibold text-muted-foreground text-xs">
										{category}
									</div>
									{docs.map((doc) => (
										<TooltipProvider key={doc.id}>
											<Tooltip>
												<TooltipTrigger asChild>
													<Button
														className="h-8 w-full justify-start gap-2"
														onClick={(e) => handleDocumentClick(doc, e)}
														variant="ghost"
													>
														{getDocumentIcon(doc.icon)}
														<span className="truncate">{doc.name}</span>
													</Button>
												</TooltipTrigger>
												<TooltipContent>
													<p>{doc.name}</p>
												</TooltipContent>
											</Tooltip>
										</TooltipProvider>
									))}
								</div>
							))}
					</div>
				</PopoverContent>
			</Popover>
		);
	}

	return (
		<div className="flex flex-col gap-1">
			{showLoading && loadingNode}
			{showEmpty && emptyNode}
			{documentsLoaded &&
				categorizedDocuments.map(({ category, docs }) => (
					<div key={category}>
						<div className="mt-2 px-2 py-1 font-semibold text-muted-foreground text-xs first:mt-0">
							{category}
						</div>
						{docs.map((doc) => (
							<TooltipProvider key={doc.id}>
								<Tooltip>
									<TooltipTrigger asChild>
										<Button
											className="h-8 w-full justify-start gap-2"
											onClick={(e) => handleDocumentClick(doc, e)}
											variant="ghost"
										>
											{getDocumentIcon(doc.icon)}
											<span className="truncate">{doc.name}</span>
											{doc.isExternal && <ArrowUpRight className="size-4" />}
										</Button>
									</TooltipTrigger>
									<TooltipContent>
										<p>{doc.name}</p>
									</TooltipContent>
								</Tooltip>
							</TooltipProvider>
						))}
					</div>
				))}
		</div>
	);
}

export function RightSidebar() {
	const [isCollapsed, setIsCollapsed] = useState(true);
	const isMobile = useIsMobile();
	const [open, setOpen] = useState(false);
	const fetchDocuments = useStore(coreStore, (state) => state.fetchDocuments);
	const setDocumentsSidecarOpen = useStore(
		coreStore,
		(state) => state.setDocumentsSidecarOpen
	);
	const wasOpenRef = useRef(false);

	// Lazy load (D3). The persistent documents panel is the desktop expanded panel
	// or the mobile sheet. Report its open-state to the store and fetch only on
	// the closed-to-open transition. This deliberately does NOT fetch on a
	// workspace switch, which the sequential chain owns, folding document.list in
	// as a final step when documentsSidecarOpen is true. document.list resolves
	// server-side against conn.currentWorkspaceId, so a component-fired fetch
	// would race workspace.setCurrent and return the previous workspace's
	// documents. The collapsed-mode popover is a transient peek and triggers its
	// own fetch through onOpenChange in DocumentsList.
	const docsPanelOpen = isMobile ? open : !isCollapsed;
	useEffect(() => {
		setDocumentsSidecarOpen(docsPanelOpen);
		const justOpened = docsPanelOpen && !wasOpenRef.current;
		wasOpenRef.current = docsPanelOpen;
		if (justOpened) fetchDocuments();
	}, [docsPanelOpen, setDocumentsSidecarOpen, fetchDocuments]);

	if (isMobile) {
		return (
			<Sheet onOpenChange={setOpen} open={open}>
				<SheetTrigger asChild>
					<Button
						className="fixed top-2 right-2 z-40 size-10 rounded-full shadow-md"
						size="icon"
						variant="secondary"
					>
						<FileText className="size-4" />
					</Button>
				</SheetTrigger>
				<SheetContent className="w-72 p-0" side="right">
					<SheetHeader className="p-4 pb-0">
						<SheetTitle className="text-sm">Documents</SheetTitle>
						<SheetDescription className="sr-only">
							Browse available documents
						</SheetDescription>
					</SheetHeader>
					<div className="flex-1 overflow-y-auto p-4 pt-2">
						<DocumentsList isCollapsed={false} setOpen={setOpen} />
					</div>
				</SheetContent>
			</Sheet>
		);
	}

	return (
		<div
			className={cn(
				"ml-2 flex h-full flex-col gap-2 p-2 pl-0 transition-[width] duration-200 ease-linear",
				isCollapsed ? "w-14" : "w-64"
			)}
		>
			<Card
				className="flex flex-1 flex-col gap-0 overflow-hidden p-0"
				data-tour="documents-sidebar"
			>
				{isCollapsed ? (
					<>
						<div className="flex items-center justify-end p-2">
							<Button
								className="size-7 shrink-0"
								onClick={() => setIsCollapsed((c) => !c)}
								size="icon"
								variant="ghost"
							>
								<ChevronLeft className="size-4" />
							</Button>
						</div>
						<div className="flex-1 overflow-y-auto p-2 pt-0">
							<div className="flex flex-col gap-2">
								<DocumentsList isCollapsed />
							</div>
						</div>
					</>
				) : (
					<>
						<div className="flex items-center justify-between gap-2 p-2">
							<span className="px-2 font-medium text-muted-foreground text-xs">
								Documents
							</span>
							<Button
								className="size-7 shrink-0"
								onClick={() => setIsCollapsed((c) => !c)}
								size="icon"
								variant="ghost"
							>
								<ChevronRight className="size-4" />
							</Button>
						</div>
						<div className="flex-1 overflow-y-auto p-2 pt-0">
							<DocumentsList isCollapsed={false} />
						</div>
					</>
				)}
			</Card>
		</div>
	);
}
